export class Cache {
	static #localInstance;
	static #sessionInstance
	#store;

	/**
	 *
	 * @returns {Cache}
	 */
	static Local() {
		if (this.#localInstance) return this.#localInstance;

		this.#localInstance = new Cache();
		this.#localInstance.#store = localStorage;
		return this.#localInstance;
	}

	/**
	 *
	 * @returns {Cache}
	 */
	static Session() {
		if (this.#sessionInstance) return this.#sessionInstance;

		this.#sessionInstance = new Cache();
		this.#sessionInstance.#store = sessionStorage;
		return this.#sessionInstance;
	}

	set(key, value) {
		if (!key) throw new Error("Key required for Cache.Set()");
		let valueToSave = value;
		if (value instanceof Map) {
			valueToSave = Object.fromEntries(value);
		}

		valueToSave != null
			? this.#store.setItem(key, typeof value === "string" ? value : JSON.stringify(valueToSave))
			: this.#store.removeItem(key);
		return value;
	}

	get(key) {
		const item = this.#store.getItem(key);
		if (!item) return;
		try {
			return JSON.parse(item);
		}
		catch (e) {
			return item;
		}
	}
}

export class DB {
	// NOTE: need to change if structure of DB changes; will recognize and call onupgradeneeded
	static #DB_VERSION = 4;
	static #DEFAULT_TTL = 21_600;  // 6 hours

	static Make_Name(id, prefix = undefined) {
		const dbPrefix = prefix ?? "catalog";
		return `${dbPrefix}_${id}`;
	}

	/**
	 * @param {String} dbName
	 * @param {string|string[]} key
	 * @param {*} value 
	 * @param {int} ttl_seconds Defaults to 6 hours
	 */
	static async Set(dbName, key, value, ttl_seconds = undefined) {
		if (!dbName) {
			console.error("Invalid DB name", dbName);
			return;
		}

		const db = await this.#GetConnection(dbName);
		if (!db) {
			console.error("Invalid DB", dbName);
			return;
		}
		const store = this.#GetStore(db);
		
		if (value == null) {
			const exists = await store.get(key);
			exists && store.delete(key);
		}
		else {
			const expires = Date.now() + ((ttl_seconds ?? this.#DEFAULT_TTL) * 1_000);
			
			store.put({ key, expires, value });
			return value;
		}
	}

	/**
	 * @param {string} dbName
	 * @param {string|string[]} key
	 */
	static Get(dbName, key) {
		if (!dbName) {
			return Promise.reject("Invalid DB name");
		}

		return new Promise((resolve, reject) => {
			this.#GetConnection(dbName).then(db => {
				if (!db) {
					return reject("Invalid DB");
				}
				const store = this.#GetStore(db);

				const request = store.get(key);
				request.onsuccess = () => {
					const { key: recordKey, value, expires } = request.result ?? {};
					if (expires && expires <= Date.now()) {
						store.delete(recordKey);
						return resolve();
					}
					resolve(value);
				}
				request.onerror = event => {
					reject(event);
				};
			});
		})
	}
	
	static async Remove(dbName) {
		window.indexedDB.deleteDatabase(dbName);
	}
	
	static async RemoveAll() {
		const dbs = await window.indexedDB.databases();
		await Promise.allSettled(dbs.map( db => this.Remove(db.name)));
	}

	/**
	 * @param {string} dbName
	 * @returns {Promise<IDBDatabase>}
	 */
	static #GetConnection(dbName) {
		return new Promise((resolve, reject) => {
			const request = window.indexedDB.open(dbName, this.#DB_VERSION);
			request.onerror = reject;
			request.onsuccess = event => {
				resolve(request.result);
			};

			request.onupgradeneeded = (event) => {
				const db = event.target.result;

				const store = db.createObjectStore("cache", { keyPath: "key" });
				store.transaction.oncomplete = () => resolve(db);
				store.transaction.onerror = reject;
			};
		});
	}

	/**
	 * @param {IDBDatabase} db
	 * @param {"read"|"readwrite"} [permissions]
	 * @returns {IDBObjectStore}
	 */
	static #GetStore(db, permissions = undefined) {
		const store = db.transaction(["cache"], permissions ?? "readwrite").objectStore("cache");
		db.close();
		return store;
	}
}